/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import useOpenGoogleReviewModal from '../GoogleReview/useOpenGoogleReviewModal';
import GoogleIcon from '../icons/GoogleIcon';
import RatingStarIcon from '../icons/RatingStarIcon';
import { TRANSLATIONS } from './i18n';
import {
  formatCount,
  getStarFill,
  MAX_RATING,
  normalizeScore,
} from './utils';

interface GoogleRatingProps {
  rateScore: number;
  rateCount?: number;
  productId?: string;
  disableClick?: boolean;
  labelSize?: 'small' | 'medium';
  onClick?: () => void;
}

export const GoogleRating: React.FC<GoogleRatingProps> = ({
  rateScore,
  rateCount,
  productId,
  disableClick = false,
  labelSize = 'small',
  onClick,
}) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);
  const { openModal } = useOpenGoogleReviewModal();

  const score = normalizeScore(rateScore);

  const content = (
    <>
      <span className="mhg-alpha-typography-body-1-relaxed text-campaign/orange-2">
        {rateScore.toFixed(1)}
      </span>
      <div className="flex flex-col items-start justify-center">
        <div className="flex items-center" aria-hidden="true">
          {Array.from({ length: MAX_RATING }, (_, index) => (
            <RatingStarIcon
              key={index}
              fill={getStarFill(score, index)}
              sizeClassName="size-size/icon-xs"
            />
          ))}
        </div>
        {rateCount ? (
          <div className="flex items-center gap-spacing/gap/soft-4px">
            <span className={classNames('mhg-alpha-caption uppercase text-text/secondary-lighter', {
              'text-[7px]': labelSize === 'small',
              'text-[10px]': labelSize === 'medium',
            })}>
              {tr(
                'google_ratings',
                staticText,
                { count: formatCount(rateCount) },
                rateCount,
              )}
            </span>
            <GoogleIcon className="size-2 shrink-0" />
          </div>
        ) : null}
      </div>
    </>
  );

  const isClickable = !disableClick && !!productId;

  if (!isClickable) {
    return (
      <div className="flex items-center gap-spacing/gap/soft-8px">{content}</div>
    );
  }

  return (
    <button
      type="button"
      className="flex items-center gap-spacing/gap/soft-8px"
      aria-label={tr('view_reviews', staticText)}
      onClick={() => {
        onClick?.();
        openModal(productId);
      }}
    >
      {content}
    </button>
  );
};
